const RESUME_STATE_PREFIX = "exam_resume_state_";

/** Clear resume/timer localStorage for every id variant of a test attempt. */
export const clearExamResumeLocalState = (...testKeys) => {
  if (typeof window === "undefined") return;

  const uniqueKeys = [
    ...new Set(
      testKeys
        .filter((value) => value != null && value !== "")
        .map(String)
    ),
  ];

  uniqueKeys.forEach((testKey) => {
    window.localStorage.removeItem(`${RESUME_STATE_PREFIX}${testKey}`);
    window.localStorage.removeItem(`exam_timeLeft_${testKey}`);
    window.localStorage.removeItem(`exam_timeSpent_${testKey}`);
  });
};

export const readResumeSnapshot = (testKey) => {
  if (testKey == null || testKey === "" || typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(`${RESUME_STATE_PREFIX}${testKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.snapshotVersion === 2 ? parsed : null;
  } catch {
    return null;
  }
};

const readResumeSnapshotMeta = (...testKeys) => {
  for (const testKey of testKeys) {
    const snapshot = readResumeSnapshot(testKey);
    if (snapshot?.meta) return snapshot.meta;
  }
  return {};
};

const isTruthy = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

export const isResumeFlagTrue = (value) =>
  value === true || value === 1 || value === "1";

export const buildExamResumeContext = (payload = {}) => {
  const raw = payload.raw || {};
  const candidateKeys = [
    payload.testKey,
    payload.setId,
    payload.testId,
    payload.paperId,
    raw.set_id,
    raw.test_id,
    raw.paper_id,
  ]
    .filter((value) => value != null && value !== "")
    .map(String);

  const meta = readResumeSnapshotMeta(...candidateKeys);

  const resolvedSetId = payload.setId ?? meta.setId ?? raw.set_id ?? raw.setId ?? null;
  const resolvedTestId = payload.testId ?? meta.testId ?? raw.test_id ?? raw.testId ?? null;
  const resolvedPaperId = payload.paperId ?? meta.paperId ?? raw.paper_id ?? raw.paperId ?? null;

  const source = payload.source ?? meta.source ?? raw.source ?? null;
  const isTopicwise =
    String(source || "").toLowerCase() === "topicwise" ||
    (resolvedSetId != null && resolvedPaperId == null && resolvedTestId == null);

  const modeKeyId = resolvedTestId ?? resolvedPaperId ?? resolvedSetId;
  let storedMode = null;
  let gamificationOn =
    payload.gamificationOn === true ||
    meta.gamificationOn === true ||
    isTruthy(raw.gamification_on ?? raw.gamificationOn);

  if (modeKeyId != null && typeof window !== "undefined") {
    storedMode = window.localStorage.getItem(`exam_mode_${modeKeyId}`);
    if (window.localStorage.getItem(`exam_gamification_${modeKeyId}`) === "1") {
      gamificationOn = true;
    }
  }

  const practiceModeRaw =
    payload.practiceMode ??
    meta.practiceMode ??
    storedMode ??
    raw.practice_mode ??
    raw.practiceMode ??
    raw.mode ??
    "test";
  const practiceMode =
    String(practiceModeRaw).toLowerCase() === "practice" ? "practice" : "test";

  const paperType =
    payload.paperType ??
    meta.paperType ??
    raw.paper_type ??
    raw.paperType ??
    null;

  const testKey =
    payload.testKey ??
    (resolvedSetId != null ? String(resolvedSetId) : null) ??
    (resolvedTestId != null ? String(resolvedTestId) : null) ??
    (resolvedPaperId != null ? String(resolvedPaperId) : null);

  const ids = isTopicwise
    ? {
        paperId: undefined,
        testId: undefined,
        setId: resolvedSetId,
      }
    : {
        paperId: resolvedPaperId ?? resolvedTestId ?? undefined,
        testId: resolvedTestId ?? resolvedPaperId ?? undefined,
        setId: resolvedSetId,
      };

  return {
    ...ids,
    paperType,
    plannerId: payload.plannerId ?? meta.plannerId ?? raw.planner_id ?? raw.plannerId ?? null,
    plannerTypeId:
      payload.plannerTypeId ??
      meta.plannerTypeId ??
      raw.planner_type_id ??
      raw.plannerTypeId ??
      null,
    categoryId:
      payload.categoryId ?? meta.categoryId ?? raw.category_id ?? raw.categoryId ?? "",
    practiceMode,
    gamificationOn: practiceMode === "practice" ? gamificationOn : false,
    source,
    testKey,
    isResume: true,
    forceFreshStart: false,
  };
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clampPercent = (value) => Math.min(100, Math.max(0, Math.round(value)));

export const getResumeRemainingLabel = (progress) => {
  const safeProgress = clampPercent(progress);
  if (safeProgress <= 0) return "In progress";
  if (safeProgress >= 100) return "Almost done";
  return `${100 - safeProgress}% remaining`;
};

export const getResumeProgressFromCounts = (attempted, total) => {
  const safeTotal = toNumber(total, 0);
  const safeAttempted = toNumber(attempted, 0);
  if (safeTotal <= 0) return 0;
  return clampPercent((safeAttempted / safeTotal) * 100);
};

export const getResumeProgressFromSnapshot = (snapshot) => {
  const questions = Array.isArray(snapshot?.questions) ? snapshot.questions : [];
  if (!questions.length) return 0;

  const attempted = questions.filter((question) => {
    const status = String(question?.question_status || question?.status || "").toLowerCase();
    if (status.includes("attempt") || status.includes("answer") || status.includes("mark")) {
      return true;
    }
    if (question?.selectedOption != null && question.selectedOption !== "") return true;
    if (question?.integerAnswer != null && String(question.integerAnswer).trim() !== "") {
      return true;
    }
    return question?.marked === true;
  }).length;

  return getResumeProgressFromCounts(attempted, questions.length);
};

export const sortResumeTestsByRecency = (items = []) => {
  if (!Array.isArray(items)) return [];

  return [...items].sort(
    (left, right) => getResumeItemUpdatedAt(right) - getResumeItemUpdatedAt(left)
  );
};

const parseResumeTimestamp = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsedDate = Date.parse(value);
  if (Number.isFinite(parsedDate)) return parsedDate;
  const parsedNumber = Number(value);
  return Number.isFinite(parsedNumber) && parsedNumber > 0 ? parsedNumber : 0;
};

const getRecordUpdatedAt = (record = {}) => {
  const candidates = [
    record.updated_at,
    record.updatedAt,
    record.last_updated,
    record.lastUpdated,
    record.modified_at,
    record.exit_time,
    record.exitTime,
    record.last_attempt_at,
    record.resume_at,
    record.created_at,
    record.test_date,
  ];

  return candidates.reduce(
    (latest, value) => Math.max(latest, parseResumeTimestamp(value)),
    0
  );
};

export const getResumeItemUpdatedAt = (item = {}) => {
  const fromItem = parseResumeTimestamp(item.updatedAt);
  if (fromItem > 0) return fromItem;
  return getRecordUpdatedAt(item.payload?.raw || {});
};

export const limitRecentResumeTests = (items = []) => sortResumeTestsByRecency(items);

const normalizeResumeTestRecord = (record, index) => {
  if (!record || typeof record !== "object") return null;

  const title =
    record.title ??
    record.test_title ??
    record.paper_name ??
    record.test_name ??
    record.name;
  if (!title) return null;

  const totalQuestions = toNumber(
    record.total_questions ?? record.totalQuestions ?? record.question_count,
    0
  );
  const attemptedQuestions = toNumber(
    record.attempted_questions ??
      record.questions_attempted ??
      record.attempted_count ??
      record.attempted,
    0
  );

  const progress = clampPercent(
    toNumber(record.progress_percent ?? record.progress ?? record.completion_percent, NaN) ||
      getResumeProgressFromCounts(attemptedQuestions, totalQuestions) ||
      50
  );

  const paperId = record.paper_id ?? record.paperId ?? null;
  const testId = record.test_id ?? record.testId ?? paperId;
  const setId = record.set_id ?? record.setId ?? null;
  const paperType = record.paper_type ?? record.paperType ?? null;
  const practiceMode = record.practice_mode ?? record.practiceMode ?? null;
  const isPractice =
    String(practiceMode || record.mode || "").toLowerCase() === "practice" ||
    isTruthy(record.is_practice);

  const id =
    record.id ??
    record.resume_id ??
    (setId != null ? `set-${setId}` : null) ??
    (testId != null ? `test-${testId}` : null) ??
    (paperId != null ? `paper-${paperId}` : null) ??
    `resume-test-${index}`;

  const subtitle =
    record.subtitle ??
    record.test_code ??
    record.paper_code ??
    record.test_type_name ??
    record.category_name ??
    record.source ??
    (isPractice ? "Practice" : "Test");

  return {
    id: String(id),
    type: isPractice ? "practice" : "test",
    title: String(title).trim(),
    subtitle: String(subtitle).trim(),
    progress,
    remainingLabel: getResumeRemainingLabel(progress),
    actionLabel: isPractice ? "Resume Practice" : "Resume Test",
    updatedAt: getRecordUpdatedAt(record),
    payload: {
      resumeKind: "exam",
      paperId,
      testId,
      setId,
      paperType,
      practiceMode: isPractice ? "practice" : practiceMode || "test",
      plannerId: record.planner_id ?? record.plannerId ?? null,
      plannerTypeId: record.planner_type_id ?? record.plannerTypeId ?? null,
      categoryId: record.category_id ?? record.categoryId ?? "",
      source: record.source ?? null,
      gamificationOn: isTruthy(record.gamification_on ?? record.gamificationOn),
      raw: record,
    },
  };
};

export const normalizeResumeTestsFromApi = (data) => {
  const rawList =
    data?.tests ??
    data?.test_list ??
    data?.resume_tests ??
    data?.ongoing_tests ??
    data?.data ??
    (Array.isArray(data) ? data : []);

  if (!Array.isArray(rawList)) return [];

  return rawList
    .map((record, index) => normalizeResumeTestRecord(record, index))
    .filter(Boolean);
};

export const mapLegacyUserTestToResumeItem = (test) => {
  if (!test?.is_resume || test?.is_attempt) return null;

  const progress = getResumeProgressFromCounts(
    test?.attempted_questions ?? test?.attempted_count,
    test?.total_questions
  );

  return {
    id: `legacy-${test.paper_id}`,
    type: "legacy_exam",
    title: test.paper_name || "In-progress Test",
    subtitle: test.test_type || test.paper_type || "Test",
    progress: progress || 50,
    remainingLabel: getResumeRemainingLabel(progress || 50),
    actionLabel: "Resume Test",
    updatedAt: getRecordUpdatedAt(test),
    payload: {
      resumeKind: "legacy_exam",
      raw: test,
    },
  };
};

export const mapSnapshotToResumeItem = (testKey, snapshot) => {
  if (!testKey || !snapshot || snapshot.snapshotVersion !== 2) return null;

  const meta = snapshot.meta || {};
  const title = meta.title || meta.paper_name || meta.test_name || "In-progress Test";
  const isPractice =
    String(meta.practiceMode || "").toLowerCase() === "practice" ||
    meta.source === "topicwise";

  const progress = getResumeProgressFromSnapshot(snapshot);

  return {
    id: `snapshot-${testKey}`,
    type: isPractice ? "practice" : "test",
    title,
    subtitle: meta.subtitle || meta.source || (isPractice ? "Practice" : "Test"),
    progress,
    remainingLabel: getResumeRemainingLabel(progress),
    actionLabel: isPractice ? "Resume Practice" : "Resume Test",
    updatedAt: parseResumeTimestamp(snapshot.updatedAt),
    payload: {
      resumeKind: "exam",
      paperId: meta.paperId ?? null,
      testId: meta.testId ?? testKey,
      setId: meta.setId ?? null,
      paperType: meta.paperType ?? null,
      practiceMode: meta.practiceMode ?? (isPractice ? "practice" : "test"),
      plannerId: meta.plannerId ?? null,
      plannerTypeId: meta.plannerTypeId ?? null,
      categoryId: meta.categoryId ?? "",
      source: meta.source ?? null,
      gamificationOn: meta.gamificationOn === true,
      testKey: String(testKey),
    },
  };
};

export const isPracticeTestListResumeTest = (test) => {
  if (!test || typeof test !== "object") return false;
  if (isTruthy(test.is_submited) || isTruthy(test.is_submitted)) return false;

  return (
    isResumeFlagTrue(test.is_resume) ||
    test.show_resume === 1 ||
    (test.is_start_test === 1 && !isTruthy(test.is_submited) && !isTruthy(test.is_submitted)) ||
    (test.is_attempted === 1 && !isTruthy(test.is_submited) && !isTruthy(test.is_submitted))
  );
};

/** @deprecated use isPracticeTestListResumeTest */
export const isPyqPaperListingResumeTest = isPracticeTestListResumeTest;

export const isTestDetailsResumeTest = (details) =>
  Boolean(details) && isResumeFlagTrue(details.is_resume);

export const shouldCheckResumeStatus = (test) => {
  if (!test || typeof test !== "object") return false;
  if (isTruthy(test.is_submited) || isTruthy(test.is_submitted)) return false;

  return (
    isResumeFlagTrue(test.is_resume) ||
    test.show_resume === 1 ||
    test.is_start_test === 1 ||
    test.is_attempted === 1
  );
};

export const isUnifiedResumeTest = ({ listTest, testDetails } = {}) =>
  isPracticeTestListResumeTest(listTest) || isTestDetailsResumeTest(testDetails);

export const resolveResumeTestTitle = (listTest, testDetails) =>
  testDetails?.paper_name ||
  listTest?.description ||
  listTest?.test_name ||
  listTest?.paper_name ||
  testDetails?.test_name ||
  null;

export const resolveResumeTestSubtitle = (listTest, testDetails, plannerConfig = {}) => {
  const formatLabel = String(testDetails?.format || listTest?.format || "")
    .split("[")[0]
    .trim();
  if (formatLabel) return formatLabel;

  const modeLabel = String(listTest?.mode || listTest?.test_type || "").trim();
  if (modeLabel && modeLabel.toLowerCase() !== "test") return modeLabel;

  if (plannerConfig.type === "year wise" || plannerConfig.parentName?.toLowerCase?.().includes("pyq")) {
    return "PYQ";
  }

  return "Test";
};

export const buildTestDetailsRequestFromListTest = (test, plannerConfig = {}) => ({
  paperType: plannerConfig.paperType,
  paperId: test.paper_id ?? test.test_id ?? undefined,
  testId: test.test_id ?? test.paper_id ?? undefined,
  setId: test.set_id ?? undefined,
  plannerId: plannerConfig.plannerId ?? undefined,
  practiceMode: "test",
  gamificationOn: false,
});

export const mapUnifiedResumeTestToResumeItem = (
  listTest,
  testDetails,
  plannerConfig = {},
  extraContext = {}
) => {
  if (!isUnifiedResumeTest({ listTest, testDetails })) return null;

  const title = resolveResumeTestTitle(listTest, testDetails);
  if (!title) return null;

  const testId = listTest?.test_id ?? listTest?.paper_id ?? testDetails?.test_id ?? testDetails?.paper_id;
  const paperId = listTest?.paper_id ?? testDetails?.paper_id ?? testId;
  const setId = listTest?.set_id ?? testDetails?.set_id ?? extraContext.setId ?? null;
  const paperType = plannerConfig.paperType ?? extraContext.paperType ?? null;
  const isPractice =
    String(plannerConfig.type || extraContext.practiceMode || "").toLowerCase() === "topic wise" ||
    String(extraContext.practiceMode || "").toLowerCase() === "practice" ||
    extraContext.source === "topicwise";

  const snapshot = extraContext.testKey ? readResumeSnapshot(extraContext.testKey) : null;
  const progress =
    (snapshot ? getResumeProgressFromSnapshot(snapshot) : 0) ||
    getResumeProgressFromCounts(
      listTest?.attempted_questions ?? listTest?.questions_attempted,
      listTest?.total_questions ?? listTest?.question_count
    ) ||
    50;

  return {
    id: extraContext.testKey
      ? `resume-${extraContext.testKey}`
      : `resume-${testId ?? paperId ?? setId}`,
    type: isPractice ? "practice" : "test",
    title: String(title).trim(),
    subtitle: resolveResumeTestSubtitle(listTest, testDetails, plannerConfig),
    progress,
    remainingLabel: getResumeRemainingLabel(progress),
    actionLabel: isPractice ? "Resume Practice" : "Resume Test",
    updatedAt:
      extraContext.updatedAt ||
      getRecordUpdatedAt(listTest || {}) ||
      getRecordUpdatedAt(testDetails || {}),
    payload: {
      resumeKind: "exam",
      paperId,
      testId,
      setId,
      paperType,
      practiceMode: isPractice ? "practice" : extraContext.practiceMode || "test",
      plannerId: plannerConfig.plannerId ?? extraContext.plannerId ?? testDetails?.planner_id ?? null,
      plannerTypeId: plannerConfig.plannerTypeId ?? extraContext.plannerTypeId ?? null,
      categoryId: plannerConfig.categoryId ?? extraContext.categoryId ?? "",
      source:
        extraContext.source ??
        (plannerConfig.type === "year wise" ? "yearwise" : "paperlisting"),
      gamificationOn: extraContext.gamificationOn === true,
      testKey: extraContext.testKey ? String(extraContext.testKey) : String(testId ?? paperId ?? setId),
      plannerCourseStream: plannerConfig.courseStream ?? null,
      plannerParentName: plannerConfig.parentName ?? null,
      plannerFormat: plannerConfig.format ?? null,
      raw: listTest || testDetails,
    },
  };
};

export const mapPyqPaperListingTestToResumeItem = (test, plannerConfig = {}) =>
  mapUnifiedResumeTestToResumeItem(test, null, plannerConfig);

export const normalizeTestDetailsResponse = (data) => {
  if (!data || typeof data !== "object") return null;

  const nestedData =
    data.data && typeof data.data === "object" && !Array.isArray(data.data) ? data.data : null;
  const merged = nestedData ? { ...data, ...nestedData } : data;

  if (
    merged.status != null &&
    Number(merged.status) !== 200 &&
    !merged.paper_name &&
    !isResumeFlagTrue(merged.is_resume)
  ) {
    return null;
  }

  const instruction =
    merged.instruction && typeof merged.instruction === "object"
      ? merged.instruction
      : merged;

  return {
    ...merged,
    ...instruction,
    is_resume: instruction.is_resume ?? merged.is_resume ?? nestedData?.is_resume,
    paper_name: instruction.paper_name ?? merged.paper_name ?? nestedData?.paper_name,
    paper_id: instruction.paper_id ?? merged.paper_id ?? nestedData?.paper_id,
    format: instruction.format ?? merged.format ?? nestedData?.format,
  };
};

export const mapTestDetailsToResumeItem = (details, context = {}) =>
  mapUnifiedResumeTestToResumeItem(null, details, {}, context);

export const collectResumeTestDetailRequests = () => {
  if (typeof window === "undefined") return [];

  const requests = [];
  const seen = new Set();

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key?.startsWith(RESUME_STATE_PREFIX)) continue;

    const testKey = key.slice(RESUME_STATE_PREFIX.length);
    const snapshot = readResumeSnapshot(testKey);
    const meta = snapshot?.meta;
    if (!meta?.paperType) continue;

    const dedupeKey = `${meta.paperType}-${meta.setId ?? meta.testId ?? meta.paperId ?? testKey}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    requests.push({
      paperType: meta.paperType,
      paperId: meta.paperId ?? undefined,
      testId: meta.testId ?? meta.paperId ?? undefined,
      setId: meta.setId ?? undefined,
      plannerId: meta.plannerId ?? undefined,
      practiceMode: meta.practiceMode,
      gamificationOn: meta.gamificationOn === true,
      plannerTypeId: meta.plannerTypeId ?? null,
      categoryId: meta.categoryId ?? "",
      source: meta.source ?? null,
      testKey: String(testKey),
      updatedAt: parseResumeTimestamp(snapshot.updatedAt),
    });
  }

  return requests.sort((left, right) => right.updatedAt - left.updatedAt);
};

export const collectLocalStorageResumeTests = () => {
  if (typeof window === "undefined") return [];

  const items = [];

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key?.startsWith(RESUME_STATE_PREFIX)) continue;

    const testKey = key.slice(RESUME_STATE_PREFIX.length);
    if (!testKey) continue;

    try {
      const snapshot = JSON.parse(window.localStorage.getItem(key));
      const item = mapSnapshotToResumeItem(testKey, snapshot);
      if (item) items.push(item);
    } catch {
      // ignore malformed snapshots
    }
  }

  return items;
};

export const dedupeResumeItems = (items = []) => {
  const byKey = new Map();

  items.forEach((item) => {
    const payload = item?.payload || {};
    const dedupeKey =
      payload.testKey ??
      (payload.setId != null ? `set:${payload.setId}` : null) ??
      (payload.testId != null ? `test:${payload.testId}` : null) ??
      (payload.paperId != null ? `paper:${payload.paperId}` : null) ??
      item.id;

    if (!dedupeKey) return;

    const existing = byKey.get(dedupeKey);
    if (!existing || getResumeItemUpdatedAt(item) >= getResumeItemUpdatedAt(existing)) {
      byKey.set(dedupeKey, item);
    }
  });

  return Array.from(byKey.values());
};

export const isIncompleteResumeTest = (test) => {
  if (!test || typeof test !== "object") return false;

  if (isTruthy(test.is_submited) || isTruthy(test.is_submitted)) return false;
  if (isTruthy(test.show_resume)) return true;
  if (isTruthy(test.is_resume)) return true;

  return (
    (isTruthy(test.is_start_test) || isTruthy(test.is_attempted)) &&
    !isTruthy(test.is_submited)
  );
};
